import React, { useState } from "react";
import { toast } from "sonner";
import {
    PenLine,
    ShieldCheck,
    Save,
    X,
    Loader2,
    Lock,
    CheckCircle2,
    Clock,
    History,
} from "lucide-react";
import { useAuth, roleAtLeast } from "../lib/auth";
import { updateFinding, approveFinding } from "../lib/api";

const REVIEW_PILL_STYLE = {
    Draft: "border-hair bg-neutral-50 text-neutral-600",
    "Pending Review":
        "border-[#FED7AA] bg-[#FFEDD5] text-[#9A3412]",
    Approved: "border-[#86EFAC] bg-[#DCFCE7] text-[#166534]",
};
const REVIEW_ICON = {
    Draft: PenLine,
    "Pending Review": Clock,
    Approved: CheckCircle2,
};

export const ReviewStatusPill = ({ status }) => {
    const s = status || "Draft";
    const Icon = REVIEW_ICON[s] || PenLine;
    return (
        <span
            data-testid={`review-status-pill-${s.replace(" ", "-")}`}
            className={`mono text-[10px] uppercase tracking-wider px-2 py-[2px] border rounded-sm inline-flex items-center gap-1 ${REVIEW_PILL_STYLE[s] || REVIEW_PILL_STYLE.Draft}`}
            title={
                s === "Approved"
                    ? "QA Manager has e-signed this finding (21 CFR Part 11)"
                    : s === "Pending Review"
                      ? "Auditor edit pending QA Manager approval"
                      : "Draft — auto-generated, not yet reviewed"
            }
        >
            <Icon size={11} />
            {s}
        </span>
    );
};

const SEVERITIES = ["Critical", "Major", "Minor", "Observation"];
const STATUSES = ["Open", "Closed"];

/**
 * Inline review controls embedded in an expanded finding row.
 * - QAAuditor+: edit severity / status / title / description / capa.
 * - QAManager+: e-sign approve.
 * - Observer: read-only — controls hidden.
 */
export default function FindingReviewControls({ finding, onChange }) {
    const { user } = useAuth();
    const role = user?.role;
    const canEdit = roleAtLeast(role, "QAAuditor");
    const canApprove = roleAtLeast(role, "QAManager");

    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(finding);
    const [saving, setSaving] = useState(false);

    const [signOpen, setSignOpen] = useState(false);
    const [signerName, setSignerName] = useState(user?.name || user?.email || "");
    const [meaning, setMeaning] = useState("Reviewed and approved");
    const [password, setPassword] = useState("");
    const [signing, setSigning] = useState(false);

    const reset = () => {
        setDraft(finding);
        setEditing(false);
    };

    const handleSave = async () => {
        const payload = {};
        for (const k of ["severity", "status", "title", "description", "capa"]) {
            if ((draft[k] || "") !== (finding[k] || "")) {
                payload[k] = draft[k];
            }
        }
        if (!Object.keys(payload).length) {
            setEditing(false);
            return;
        }
        setSaving(true);
        try {
            const updated = await updateFinding(finding.id, payload);
            toast.success("Finding updated · Pending Review");
            onChange?.(updated);
            setEditing(false);
        } catch (e) {
            toast.error(
                e?.response?.status === 403
                    ? "Read-only demo — editing disabled."
                    : e?.response?.data?.detail || "Update failed",
            );
        } finally {
            setSaving(false);
        }
    };

    const handleApprove = async () => {
        if (!signerName.trim() || !password.trim()) {
            toast.error("Signer name and password required (21 CFR Part 11).");
            return;
        }
        setSigning(true);
        try {
            const result = await approveFinding(finding.id, {
                signer_name: signerName.trim(),
                meaning: meaning.trim() || "Reviewed and approved",
                password,
            });
            toast.success("Finding e-signed and approved.");
            onChange?.({ ...finding, ...result });
            setSignOpen(false);
            setPassword("");
        } catch (e) {
            toast.error(
                e?.response?.status === 403
                    ? "Read-only demo — approval disabled."
                    : e?.response?.data?.detail || "Approval failed",
            );
        } finally {
            setSigning(false);
        }
    };

    const sig = finding.review_signature;
    const edits = finding.edits || [];

    return (
        <div
            className="mt-6 border-t border-hair pt-5"
            data-testid={`finding-review-controls-${finding.finding_id}`}
        >
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="overline flex items-center gap-2">
                    Review status <ReviewStatusPill status={finding.review_status} />
                </div>
                {canEdit && !editing && (
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => {
                                setDraft(finding);
                                setEditing(true);
                            }}
                            data-testid={`btn-edit-finding-${finding.finding_id}`}
                            className="inline-flex items-center gap-2 border border-hair text-xs px-3 py-1.5 rounded-sm hover:bg-white"
                        >
                            <PenLine size={12} /> Edit
                        </button>
                        {canApprove && finding.review_status !== "Approved" && (
                            <button
                                type="button"
                                onClick={() => setSignOpen(true)}
                                data-testid={`btn-approve-finding-${finding.finding_id}`}
                                className="inline-flex items-center gap-2 bg-[#0a0a0a] text-white text-xs px-3 py-1.5 rounded-sm hover:bg-[#262626]"
                            >
                                <ShieldCheck size={12} /> Approve &amp; Sign
                            </button>
                        )}
                    </div>
                )}
            </div>

            {editing && (
                <div
                    className="mt-4 p-5 border border-hair bg-white"
                    data-testid={`finding-edit-form-${finding.finding_id}`}
                >
                    <div className="grid md:grid-cols-2 gap-4">
                        <label className="text-[12px] flex flex-col gap-1">
                            <span className="overline">Severity</span>
                            <select
                                value={draft.severity}
                                onChange={(e) =>
                                    setDraft({ ...draft, severity: e.target.value })
                                }
                                data-testid={`edit-severity-${finding.finding_id}`}
                                className="border border-hair bg-white px-3 py-2 rounded-sm text-sm focus:outline-none focus:border-[#C2410C]"
                            >
                                {SEVERITIES.map((s) => (
                                    <option key={s} value={s}>
                                        {s}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label className="text-[12px] flex flex-col gap-1">
                            <span className="overline">Status</span>
                            <select
                                value={draft.status}
                                onChange={(e) =>
                                    setDraft({ ...draft, status: e.target.value })
                                }
                                data-testid={`edit-status-${finding.finding_id}`}
                                className="border border-hair bg-white px-3 py-2 rounded-sm text-sm focus:outline-none focus:border-[#C2410C]"
                            >
                                {STATUSES.map((s) => (
                                    <option key={s} value={s}>
                                        {s}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>
                    <label className="text-[12px] flex flex-col gap-1 mt-4">
                        <span className="overline">Title</span>
                        <input
                            type="text"
                            value={draft.title || ""}
                            onChange={(e) =>
                                setDraft({ ...draft, title: e.target.value })
                            }
                            data-testid={`edit-title-${finding.finding_id}`}
                            className="border border-hair bg-white px-3 py-2 rounded-sm text-sm focus:outline-none focus:border-[#C2410C]"
                        />
                    </label>
                    <label className="text-[12px] flex flex-col gap-1 mt-4">
                        <span className="overline">Description</span>
                        <textarea
                            rows={3}
                            value={draft.description || ""}
                            onChange={(e) =>
                                setDraft({ ...draft, description: e.target.value })
                            }
                            data-testid={`edit-description-${finding.finding_id}`}
                            className="border border-hair bg-white px-3 py-2 rounded-sm text-sm focus:outline-none focus:border-[#C2410C]"
                        />
                    </label>
                    <label className="text-[12px] flex flex-col gap-1 mt-4">
                        <span className="overline">CAPA</span>
                        <textarea
                            rows={3}
                            value={draft.capa || ""}
                            onChange={(e) =>
                                setDraft({ ...draft, capa: e.target.value })
                            }
                            data-testid={`edit-capa-${finding.finding_id}`}
                            className="border border-hair bg-white px-3 py-2 rounded-sm text-sm focus:outline-none focus:border-[#C2410C]"
                        />
                    </label>
                    <div className="flex justify-end gap-2 mt-4">
                        <button
                            type="button"
                            onClick={reset}
                            disabled={saving}
                            className="inline-flex items-center gap-2 border border-hair text-xs px-3 py-1.5 rounded-sm hover:bg-neutral-50"
                        >
                            <X size={12} /> Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={saving}
                            data-testid={`btn-save-finding-${finding.finding_id}`}
                            className="inline-flex items-center gap-2 bg-[#C2410C] text-white text-xs px-3 py-1.5 rounded-sm hover:bg-[#9A3412] disabled:opacity-50"
                        >
                            {saving ? (
                                <Loader2 size={12} className="animate-spin" />
                            ) : (
                                <Save size={12} />
                            )}
                            Save &amp; submit for review
                        </button>
                    </div>
                    <p className="text-[11px] text-neutral-500 mt-3 leading-relaxed">
                        Saving any change resets review status to{" "}
                        <span className="mono">Pending Review</span> and
                        invalidates the existing signature (kept in audit
                        history).
                    </p>
                </div>
            )}

            {sig && (
                <div
                    className="mt-4 p-4 bg-[#F0FDF4] border border-[#86EFAC] rounded-sm"
                    data-testid={`finding-signature-${finding.finding_id}`}
                >
                    <div className="flex items-center gap-2 overline text-[#166534]">
                        <Lock size={11} /> 21 CFR Part 11 e-signature
                    </div>
                    <dl className="grid md:grid-cols-2 gap-x-6 gap-y-1 mt-3 text-[12px]">
                        <div className="flex justify-between gap-3">
                            <dt className="text-neutral-500">Signer</dt>
                            <dd className="heading font-medium">
                                {sig.signer_name}
                            </dd>
                        </div>
                        <div className="flex justify-between gap-3">
                            <dt className="text-neutral-500">Role</dt>
                            <dd className="mono">{sig.signer_role}</dd>
                        </div>
                        <div className="flex justify-between gap-3">
                            <dt className="text-neutral-500">Meaning</dt>
                            <dd>{sig.meaning}</dd>
                        </div>
                        <div className="flex justify-between gap-3">
                            <dt className="text-neutral-500">Signed at</dt>
                            <dd className="mono text-[11px]">
                                {sig.signed_at
                                    ? new Date(sig.signed_at).toLocaleString()
                                    : "—"}
                            </dd>
                        </div>
                        <div className="md:col-span-2 flex justify-between gap-3">
                            <dt className="text-neutral-500">SHA-256</dt>
                            <dd className="mono text-[11px] truncate">
                                {(sig.content_hash || "").slice(0, 32)}…
                            </dd>
                        </div>
                    </dl>
                </div>
            )}

            {!!edits.length && (
                <div className="mt-4">
                    <div className="overline mb-2 flex items-center gap-2">
                        <History size={11} /> Audit trail · {edits.length} edit
                        {edits.length === 1 ? "" : "s"}
                    </div>
                    <ul
                        className="text-[12px] space-y-1.5 mono text-neutral-600 max-h-40 overflow-auto pr-2"
                        data-testid={`finding-audit-trail-${finding.finding_id}`}
                    >
                        {edits.slice().reverse().map((e, i) => (
                            <li key={i} className="leading-relaxed">
                                <span className="text-neutral-400">
                                    {e.at
                                        ? new Date(e.at).toLocaleString()
                                        : "—"}
                                </span>{" "}
                                · <span>{e.by_user_name}</span>{" "}
                                <span className="text-neutral-400">
                                    ({e.by_role})
                                </span>{" "}
                                changed <span className="font-medium">{e.field}</span>:{" "}
                                <span className="line-through text-neutral-400">
                                    {String(e.from ?? "—").slice(0, 40)}
                                </span>{" "}
                                →{" "}
                                <span className="text-neutral-800">
                                    {String(e.to ?? "—").slice(0, 40)}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {signOpen && (
                <div
                    className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
                    onClick={() => !signing && setSignOpen(false)}
                    data-testid={`finding-sign-dialog-${finding.finding_id}`}
                >
                    <div
                        className="bg-white border border-hair max-w-md w-full p-6 rounded-sm"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="overline mb-1">21 CFR Part 11 § 11.200</div>
                        <h3 className="heading text-lg font-medium tracking-tight">
                            E-sign &amp; approve finding
                        </h3>
                        <p className="text-[12px] text-neutral-600 mt-2 leading-relaxed">
                            Re-enter your password to bind this signature to
                            the current finding state. Any future edit will
                            invalidate this signature and route back to{" "}
                            <span className="mono">Pending Review</span>.
                        </p>
                        <label className="text-[12px] flex flex-col gap-1 mt-4">
                            <span className="overline">Signer name</span>
                            <input
                                type="text"
                                value={signerName}
                                onChange={(e) => setSignerName(e.target.value)}
                                data-testid="sign-signer-name"
                                className="border border-hair px-3 py-2 rounded-sm text-sm focus:outline-none focus:border-[#C2410C]"
                            />
                        </label>
                        <label className="text-[12px] flex flex-col gap-1 mt-3">
                            <span className="overline">Meaning of signature</span>
                            <select
                                value={meaning}
                                onChange={(e) => setMeaning(e.target.value)}
                                data-testid="sign-meaning"
                                className="border border-hair bg-white px-3 py-2 rounded-sm text-sm focus:outline-none focus:border-[#C2410C]"
                            >
                                <option>Reviewed and approved</option>
                                <option>Reviewed</option>
                                <option>Approved</option>
                                <option>QA-released</option>
                            </select>
                        </label>
                        <label className="text-[12px] flex flex-col gap-1 mt-3">
                            <span className="overline">Password</span>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                data-testid="sign-password"
                                className="border border-hair px-3 py-2 rounded-sm text-sm focus:outline-none focus:border-[#C2410C]"
                                autoFocus
                            />
                        </label>
                        <div className="flex justify-end gap-2 mt-6">
                            <button
                                type="button"
                                onClick={() => setSignOpen(false)}
                                disabled={signing}
                                className="inline-flex items-center gap-2 border border-hair text-xs px-3 py-1.5 rounded-sm hover:bg-neutral-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleApprove}
                                disabled={signing}
                                data-testid="btn-confirm-sign"
                                className="inline-flex items-center gap-2 bg-[#0a0a0a] text-white text-xs px-3 py-1.5 rounded-sm hover:bg-[#262626] disabled:opacity-50"
                            >
                                {signing ? (
                                    <Loader2 size={12} className="animate-spin" />
                                ) : (
                                    <ShieldCheck size={12} />
                                )}
                                Sign &amp; approve
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
